import { useState } from "react";
import { ChatWidgetText, ReturnWidget } from "../../../Helper/ChatWidget";
import { CheckIcon, DashIcon, PlusIcon } from "../ChatWidgetIcons";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
  Receipt,
  Thumb,
} from "../ChatWidgetPrimitives";
import "./ReturnCard.scss";

type ReturnItem = ReturnWidget["items"][number];

/** What a line holds: the stepper's maximum (1 when the server sent none). */
const lineSize = (item: ReturnItem) =>
  typeof item.quantity === "number" &&
  Number.isInteger(item.quantity) &&
  item.quantity > 1
    ? item.quantity
    : 1;

const clampQuantity = (item: ReturnItem, value: unknown) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return Math.min(parsed, lineSize(item));
};

/** "1 of 2 × Merino socks", "Merino socks", for the receipt and the answer. */
const itemLabel = (item: ReturnItem, quantity: number, text: ChatWidgetText) =>
  lineSize(item) > 1 && quantity < lineSize(item)
    ? text("widgetReturnQuantityOf", {
        quantity,
        total: lineSize(item),
        name: item.title,
      })
    : item.title;

/** One fewer / one more, within 1…what the line holds. */
const QuantityStepper = ({
  item,
  value,
  onChange,
  text,
}: {
  item: ReturnItem;
  value: number;
  onChange: (value: number) => void;
  text: ChatWidgetText;
}) => (
  <span
    className="cw-return-qty"
    role="group"
    aria-label={text("widgetReturnQuantity", { name: item.title })}
  >
    <button
      type="button"
      className="cw-return-qty-btn"
      aria-label={text("widgetFewer")}
      disabled={value <= 1}
      onClick={() => onChange(value - 1)}
    >
      <DashIcon size={14} strokeWidth={2.4} />
    </button>
    <span className="cw-return-qty-value cw-num" aria-live="polite">
      {value}
    </span>
    <button
      type="button"
      className="cw-return-qty-btn"
      aria-label={text("widgetMore")}
      disabled={value >= lineSize(item)}
      onClick={() => onChange(value + 1)}
    >
      <PlusIcon size={14} strokeWidth={2.4} />
    </button>
  </span>
);

/**
 * 13 Return: tick the items (how many of each when a line holds several),
 * pick a reason, then Get return label, or Request return when the team
 * decides (Exchange when the shop allows it). What the customer already told
 * Kai comes ticked and picked.
 */
export const ReturnCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: ReturnWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const items = widget.items ?? [];
  const [picked, setPicked] = useState<Record<string, number>>(() => {
    const prefilled: Record<string, number> = {};
    for (const entry of widget.selected ?? []) {
      const item = items.find((candidate) => candidate.id === entry.id);
      if (item) prefilled[item.id] = clampQuantity(item, entry.quantity);
    }
    if (Object.keys(prefilled).length === 0 && items.length === 1) {
      prefilled[items[0].id] = 1;
    }
    return prefilled;
  });
  const [reason, setReason] = useState<string | undefined>(
    widget.reason && widget.reasons?.includes(widget.reason)
      ? widget.reason
      : undefined,
  );

  if (widget.response) {
    const values = widget.response.values ?? {};
    const ids = Array.isArray(values.items) ? values.items : [];
    const quantities = Array.isArray(values.quantities)
      ? values.quantities
      : [];
    const titles = ids
      .map((id, index) => {
        const item = items.find((entry) => entry.id === id);
        if (!item) return undefined;
        // No quantity in the answer: the line's own size (a line of one).
        return itemLabel(
          item,
          quantities[index] !== undefined
            ? clampQuantity(item, quantities[index])
            : lineSize(item),
          text,
        );
      })
      .filter(Boolean);
    const isExchange = values.exchange === "true";
    return (
      <Receipt
        title={text(
          widget.teamReviews
            ? "widgetReturnSentToTeam"
            : isExchange
              ? "widgetExchangeRequested"
              : "widgetReturnRequested",
        )}
        detail={[
          titles.join(", "),
          typeof values.reason === "string" ? values.reason : undefined,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
    );
  }

  const pickedItems = items.filter((item) => picked[item.id] !== undefined);
  const canSubmit = pickedItems.length > 0 && !!reason;
  const toggle = (id: string) =>
    setPicked((current) => {
      const next = { ...current };
      if (next[id] !== undefined) {
        delete next[id];
      } else {
        next[id] = 1;
      }
      return next;
    });
  const setQuantity = (item: ReturnItem, value: number) =>
    setPicked((current) => ({
      ...current,
      [item.id]: clampQuantity(item, value),
    }));
  const primaryLabel = text(
    widget.teamReviews ? "widgetRequestReturn" : "widgetGetReturnLabel",
  );
  const submit = (isExchange: boolean) => {
    if (!canSubmit || !reason) return;
    const ids = pickedItems.map((item) => item.id);
    // `quantities[i]` goes with `items[i]`; the server re-checks both.
    const quantities = pickedItems.map((item) => `${picked[item.id]}`);
    const titles = pickedItems.map((item) =>
      itemLabel(item, picked[item.id], text),
    );
    const action = isExchange ? text("widgetExchange") : primaryLabel;
    onAnswer(
      "submit",
      isExchange
        ? { items: ids, quantities, reason, exchange: "true" }
        : { items: ids, quantities, reason },
      `${action}: ${titles.join(", ")} · ${reason}`,
    );
  };
  const title = widget.title ?? text("widgetReturnItems");

  return (
    <Card className="cw-return" label={title}>
      <div className="cw-row cw-pad cw-return-head">
        <span className="cw-h">{title}</span>
        <span className="cw-muted cw-sm">
          {text("widgetOrder", { number: widget.orderNumber })}
        </span>
      </div>
      <ul className="cw-return-items">
        {items.map((item) => {
          const isOn = picked[item.id] !== undefined;
          const inputId = `cw-${widget.id}-item-${item.id}`;
          return (
            <li key={item.id} className="cw-return-row">
              <label className="cw-line cw-return-item" htmlFor={inputId}>
                <input
                  id={inputId}
                  type="checkbox"
                  className="cw-return-input"
                  checked={isOn}
                  onChange={() => toggle(item.id)}
                  aria-label={text("widgetReturnItem", { name: item.title })}
                />
                <span
                  className={cx("cw-check", isOn && "cw-check--on")}
                  aria-hidden="true"
                >
                  {isOn && <CheckIcon size={13} strokeWidth={3} />}
                </span>
                <Thumb image={item.image} />
                <span className="cw-stack cw-grow">
                  <span className="cw-medium">{item.title}</span>
                  {item.subtitle && (
                    <span className="cw-muted cw-sm cw-num">
                      {item.subtitle}
                    </span>
                  )}
                </span>
              </label>
              {isOn && lineSize(item) > 1 && (
                <QuantityStepper
                  item={item}
                  value={picked[item.id]}
                  onChange={(value) => setQuantity(item, value)}
                  text={text}
                />
              )}
            </li>
          );
        })}
      </ul>
      {widget.reasons && widget.reasons.length > 0 && (
        <div className="cw-return-reasons">
          <div className="cw-sm cw-muted" id={`cw-${widget.id}-reason`}>
            {text("widgetReason")}
          </div>
          <div
            className="cw-return-chips"
            role="radiogroup"
            aria-labelledby={`cw-${widget.id}-reason`}
          >
            {widget.reasons.map((entry) => (
              <button
                type="button"
                role="radio"
                aria-checked={entry === reason}
                key={entry}
                className={cx(
                  "cw-return-chip",
                  entry === reason && "cw-return-chip--on",
                )}
                onClick={() => setReason(entry)}
              >
                {entry}
              </button>
            ))}
          </div>
        </div>
      )}
      {widget.policyNote && (
        <div className="cw-sm cw-muted cw-return-note">{widget.policyNote}</div>
      )}
      <Foot>
        {widget.allowExchange && (
          <Button
            variant="ghost"
            className="cw-return-exchange"
            disabled={!canSubmit}
            onClick={() => submit(true)}
          >
            {text("widgetExchange")}
          </Button>
        )}
        <Button disabled={!canSubmit} onClick={() => submit(false)}>
          {primaryLabel}
        </Button>
      </Foot>
    </Card>
  );
};
